import React, { useCallback } from "react";
import { find, head, map } from "lodash";
import { Facet } from "@linkfluence/models/facet";
import Checkbox from "@ui-kit/form/checkbox";
import Dropdown from "@radarly/components/panels/card-edition/sidebar/dropdown";
import Line from "@radarly/components/panels/card-edition/sidebar/line";
import { useProjectFacet } from "@radarly/core/facets/use-cases";
import { useFeature } from "@radarly/core/features/use-cases";
import { useCurrentUserLocale } from "@radarly/core/user/use-cases";
import { tr } from "@radarly/i18n";
import { PivotCard } from "@radarly/models/cards";
import { getTermFromCard } from "@radarly/models/cards/term";
import {
  DatavizHorizontalStackedBarChart,
  updateDatavizOptions,
} from "@radarly/models/dataviz";
import { getTermFromAggs } from "@radarly/models/search";

export interface SharedPanelsCardEditionSidebarOptionsHorizontalStackedBarChartProps {
  dataviz: DatavizHorizontalStackedBarChart;
  setDataviz: (dataviz: DatavizHorizontalStackedBarChart) => void;
  card: PivotCard;
}

const Fields = {
  sort: "sort",
};

type Field = keyof typeof Fields;
type SelectValue = {
  value: string;
  label: string;
};

const isCustomSort = (sort) => sort && sort !== "default" && sort !== "alpha";

export default function SharedPanelsCardEditionSidebarOptionsHorizontalStackedBarChart({
  dataviz,
  setDataviz,
  card,
}: SharedPanelsCardEditionSidebarOptionsHorizontalStackedBarChartProps) {
  const isChartValuesFormatEnabled = useFeature("chart_values_format");
  const locale = useCurrentUserLocale();
  const pivot = getTermFromCard(card);
  const against = getTermFromAggs(card.fragments[0].request.aggs[pivot].aggs);
  const facet = useProjectFacet(against as Facet);
  const custom = isCustomSort(dataviz.sort);
  const options = {
    [Fields.sort]: [
      {
        value: "default",
        label: tr(locale, "CUSTOM_PAGES::EDITOR::DATAVIZ::SORT_BY_DEFAULT"),
      },
      {
        value: "alpha",
        label: tr(locale, "CUSTOM_PAGES::EDITOR::DATAVIZ::SORT_BY_NAME"),
      },
      {
        value: "custom",
        label: tr(locale, "CUSTOM_PAGES::EDITOR::DATAVIZ::CUSTOM_SORT"),
      },
    ],
  };

  const chartValuesFormatOptions = [
    {
      value: "relative",
      label: tr(
        locale,
        "CUSTOM_PAGES::EDITOR::DATAVIZ::DISPLAY_RELATIVE_VALUES"
      ),
    },
    {
      value: "absolute",
      label: tr(
        locale,
        "CUSTOM_PAGES::EDITOR::DATAVIZ::DISPLAY_ABSOLUTE_VALUES"
      ),
    },
  ];

  const getFieldValue = (type: Field) => {
    const getLabel = (_value, field) =>
      options[field].find(({ value }) => value === _value).label;
    switch (type) {
      case Fields.sort:
        return custom
          ? options[Fields.sort][2]
          : dataviz.sort &&
            options[type].some(({ value }) => value === dataviz.sort)
          ? {
              value: dataviz.sort,
              label: getLabel(dataviz.sort, Fields.sort),
            }
          : options[Fields.sort][0];
      default:
        return null;
    }
  };

  const getCustomSortValue = () => {
    const getLabel = (_value) =>
      facet.values.find(({ id }) => id === _value).label;
    if (
      dataviz.sort &&
      facet.values.some(({ id }) => id === dataviz.sort || "default")
    ) {
      return {
        value: dataviz.sort,
        label: getLabel(dataviz.sort),
      };
    }
    return null;
  };

  const getChartValuesFormatOption = () => {
    const currentOption = find(chartValuesFormatOptions, {
      value: dataviz.chartValuesFormat,
    });
    if (!currentOption) {
      return head(chartValuesFormatOptions);
    }
    return currentOption;
  };

  const handleChange = (value: string) => {
    if (value === "custom") {
      setDataviz(
        updateDatavizOptions(dataviz, "sort", "" + head(facet.values).id)
      );
    } else {
      setDataviz(updateDatavizOptions(dataviz, "sort", value));
    }
  };

  const updateDisplayVolumes = useCallback(() => {
    const value = dataviz.displayVolumes ? false : true;
    setDataviz(
      updateDatavizOptions(
        dataviz,
        "displayVolumes",
        value as DatavizHorizontalStackedBarChart["displayVolumes"]
      )
    );
  }, [dataviz, setDataviz]);

  return (
    <>
      {isChartValuesFormatEnabled && (
        <Dropdown
          label={tr(locale, "CUSTOM_PAGES::EDITOR::DATAVIZ::VALUE_DISPLAY")}
          value={getChartValuesFormatOption()}
          options={chartValuesFormatOptions}
          onChange={({ value }) =>
            setDataviz(
              updateDatavizOptions(dataviz, "chartValuesFormat", value)
            )
          }
        />
      )}
      <Dropdown
        label={tr(locale, "CUSTOM_PAGES::EDITOR::DATAVIZ::SORT")}
        value={getFieldValue(Fields.sort as Field) as SelectValue}
        options={options[Fields.sort]}
        onChange={({ value }) => handleChange(value)}
      />
      {custom && (
        <Dropdown
          label={tr(locale, "CUSTOM_PAGES::EDITOR::DATAVIZ::CUSTOM_SORT")}
          value={getCustomSortValue()}
          options={map(facet.values, ({ id, label }) => ({
            value: id.toString(),
            label,
          }))}
          onChange={({ value }) =>
            setDataviz(updateDatavizOptions(dataviz, "sort", value))
          }
        />
      )}
      <Line title={tr(locale, "CUSTOM_PAGES::EDITOR::CARD_LEGEND")}>
        <Checkbox
          label={tr(locale, "CUSTOM_PAGES::EDITOR::DATAVIZ::DISPLAY_VOLUMES")}
          checked={dataviz.displayVolumes || undefined}
          onClick={updateDisplayVolumes}
        />
      </Line>
    </>
  );
}
